import { addDays, minutesSinceMidnight, startOfDay } from '@/shared/lib'
import type { Block } from '../model/types'

export interface PositionedBlock {
  block: Block
  /** Минуты от полуночи колонки (обрезаны границами суток). */
  startMin: number
  endMin: number
  /** Колонка внутри кластера пересечений и общее число колонок кластера. */
  col: number
  cols: number
  /** Блок пересекается с соседом в этой же колонке — предупреждение, не отказ. */
  conflict: boolean
}

// Минимальная визуальная высота блока — иначе 15-минутки нечитаемы и некликабельны.
const MIN_VISUAL_MINUTES = 24

/**
 * Раскладка блоков одного дня: пересекающиеся делят ширину колонки. Кластер —
 * транзитивная группа пересечений; внутри кластера колонки раздаются жадно
 * (первая освободившаяся). Функция чистая и от смысла колонки не зависит.
 */
export function layoutColumn(blocks: Block[], day: Date): PositionedBlock[] {
  const dayStart = startOfDay(day)
  const dayEnd = addDays(dayStart, 1)

  const items: PositionedBlock[] = blocks
    .filter((b) => b.slot.startsAt < dayEnd && b.slot.endsAt > dayStart)
    .map((block) => {
      const startMin =
        block.slot.startsAt <= dayStart ? 0 : minutesSinceMidnight(block.slot.startsAt)
      const rawEnd = block.slot.endsAt >= dayEnd ? 1440 : minutesSinceMidnight(block.slot.endsAt)
      return {
        block,
        startMin,
        endMin: Math.min(1440, Math.max(rawEnd, startMin + MIN_VISUAL_MINUTES)),
        col: 0,
        cols: 1,
        conflict: false,
      }
    })
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin)

  let cluster: PositionedBlock[] = []
  let clusterEnd = -1
  // Конец занятости каждой колонки текущего кластера
  let columns: number[] = []

  const sealCluster = () => {
    // Кластер шире одной колонки — значит внутри есть настоящее наложение.
    const conflicted = columns.length > 1
    for (const item of cluster) {
      item.cols = columns.length
      item.conflict = conflicted
    }
    cluster = []
    columns = []
  }

  for (const item of items) {
    if (item.startMin >= clusterEnd && cluster.length > 0) sealCluster()
    const free = columns.findIndex((end) => end <= item.startMin)
    if (free === -1) {
      item.col = columns.length
      columns.push(item.endMin)
    } else {
      item.col = free
      columns[free] = item.endMin
    }
    cluster.push(item)
    clusterEnd = Math.max(clusterEnd, item.endMin)
  }
  sealCluster()

  return items
}
